import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Check, RefreshCw, X } from "lucide-react";
import { toast } from "react-toastify";
import { adminApi } from "../../../api/adminApi";
import { useNotifications } from "../../../context/NotificationContext";
import "./ClientDeletionRequests.css";

const ClientDeletionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [notes, setNotes] = useState({});
  const { socket } = useNotifications();

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await adminApi.getClientDeletionRequests();
      setRequests(response.data || []);
    } catch (loadError) {
      setError(
        loadError.response?.data?.error ||
          "Could not load client deletion requests.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    if (!socket) return undefined;
    const handleNotification = (notification) => {
      if (notification?.Type === "client_deletion_request") {
        loadRequests();
      }
    };
    socket.on("notification:new", handleNotification);
    socket.on("client-communications:updated", loadRequests);
    return () => {
      socket.off("notification:new", handleNotification);
      socket.off("client-communications:updated", loadRequests);
    };
  }, [loadRequests, socket]);

  const review = async (request, status) => {
    const action = status === "approved" ? "approve" : "reject";
    if (
      status === "approved" &&
      !window.confirm(
        "Approve this request for permanent deletion? Review legal, accounting, active service, and payment retention requirements before completing the hard delete.",
      )
    ) {
      return;
    }
    setBusyId(request.DeletionRequestID);
    setError("");
    try {
      const response = await adminApi.reviewClientDeletionRequest(
        request.DeletionRequestID,
        status,
        notes[request.DeletionRequestID] || "",
      );
      toast.success(
        response.message ||
          (status === "approved"
            ? "Deletion request approved."
            : "Deletion request rejected."),
      );
      if (status === "approved") {
        setRequests((current) =>
          current.map((item) =>
            item.DeletionRequestID === request.DeletionRequestID
              ? { ...item, Status: "approved" }
              : item,
          ),
        );
      } else {
        setRequests((current) =>
          current.filter(
            (item) => item.DeletionRequestID !== request.DeletionRequestID,
          ),
        );
      }
    } catch (reviewError) {
      setError(
        reviewError.response?.data?.error ||
          `Could not ${action} the deletion request.`,
      );
    } finally {
      setBusyId("");
    }
  };

  return (
    <section className="dashboard-card client-deletion-requests">
      <header className="cdr-header">
        <div>
          <h3>Account deletion requests</h3>
          <p>
            Review requests before any permanent deletion. Approval alone does
            not delete data; use the client&apos;s permanent-delete action only
            after retention checks.
          </p>
        </div>
        <button
          className="btn-ghost"
          type="button"
          onClick={loadRequests}
          disabled={loading}
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="alert alert-error" role="alert">
          <AlertTriangle size={17} />
          {error}
        </div>
      )}
      {loading ? (
        <p className="cdr-empty">Loading deletion requests...</p>
      ) : requests.length ? (
        <div className="cdr-list">
          {requests.map((request) => (
            <article className="cdr-item" key={request.DeletionRequestID}>
              <div className="cdr-details">
                <strong>{request.FullName || "Client"}</strong>
                <span>{request.Email}</span>
                {request.BusinessName && (
                  <span>Business: {request.BusinessName}</span>
                )}
                <span>
                  Requested{" "}
                  {new Date(request.RequestedAt).toLocaleString()}
                </span>
                {request.Status === "approved" && (
                  <b className="cdr-approved">
                    Approved — complete permanent deletion from the client
                    list only after retention review
                  </b>
                )}
              </div>
              {request.Status === "pending" && (
                <div className="cdr-actions">
                  <label>
                    Review note
                    <input
                      value={notes[request.DeletionRequestID] || ""}
                      maxLength={1000}
                      onChange={(event) =>
                        setNotes((current) => ({
                          ...current,
                          [request.DeletionRequestID]: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <div>
                    <button
                      className="btn-secondary"
                      type="button"
                      disabled={busyId === request.DeletionRequestID}
                      onClick={() => review(request, "rejected")}
                    >
                      <X size={15} /> Reject
                    </button>
                    <button
                      className="btn-primary"
                      type="button"
                      disabled={busyId === request.DeletionRequestID}
                      onClick={() => review(request, "approved")}
                    >
                      <Check size={15} /> Approve
                    </button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <p className="cdr-empty">There are no open deletion requests.</p>
      )}
    </section>
  );
};

export default ClientDeletionRequests;
